import React from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

function getGaugeColor(score: number): string {
  if (score <= 3) return '#ef4444';  // red
  if (score <= 5) return '#f59e0b';  // amber
  if (score <= 7) return '#22c55e';  // green
  return '#00c7c4';                   // primary teal
}

function getEngagementLabel(score: number): string {
  if (score <= 2) return 'Low';
  if (score <= 4) return 'Moderate';
  if (score <= 6) return 'Good';
  if (score <= 8) return 'High';
  return 'Excellent';
}

const EngagementGaugeWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  if (!state) return null;

  const score = state.facilitatorGuidance?.conversationHealth?.balanceScore ?? 5;
  const clampedScore = Math.max(0, Math.min(10, score));
  const color = getGaugeColor(clampedScore);
  const label = getEngagementLabel(clampedScore);

  // SVG arc gauge — 180 degree arc
  const radius = 60;
  const strokeWidth = 10;
  const cx = 75;
  const cy = 70;
  const startAngle = Math.PI;
  const endAngle = 0;
  const scoreAngle = startAngle - (clampedScore / 10) * Math.PI;

  const arcPath = (angle: number) => {
    const x = cx + radius * Math.cos(angle);
    const y = cy - radius * Math.sin(angle);
    return `${x} ${y}`;
  };

  const bgArc = `M ${arcPath(startAngle)} A ${radius} ${radius} 0 1 1 ${arcPath(endAngle)}`;
  const valueArc = `M ${arcPath(startAngle)} A ${radius} ${radius} 0 ${clampedScore > 5 ? 1 : 0} 1 ${arcPath(scoreAngle)}`;

  return (
    <div className="flex flex-col items-center justify-center h-full px-4">
      <svg viewBox="0 0 150 85" className="w-full max-w-[200px]">
        {/* Background arc */}
        <path
          d={bgArc}
          fill="none"
          stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d={valueArc}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${color}50)`, transition: 'all 0.8s ease-out' }}
        />
        {/* Score text */}
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          className={`text-2xl font-bold ${isDark ? 'fill-white' : 'fill-slate-900'}`}
          style={{ fontSize: '28px', fontFamily: 'ui-monospace, monospace' }}
        >
          {clampedScore.toFixed(1)}
        </text>
      </svg>

      <span
        className="text-xs font-semibold uppercase tracking-wider mt-1"
        style={{ color }}
      >
        {label}
      </span>
    </div>
  );
};

export default EngagementGaugeWidget;
